import { useEffect, useMemo, useRef, useState } from 'react'

import cn from 'classnames'

import { isValidArray, tr } from 'mmfintech-commons'

import { CoreInput } from '../CoreInput'
import { SelectionElement } from './SelectionElement'
import type { SelectionProps } from './selectionTypes'
import { SelectedIcon, filterOptions, selectedLabel } from './selectionUtils'

import arrowDownIcon from '../../assets/icons/chevron-down.svg'
import arrowUpIcon from '../../assets/icons/chevron-up.svg'
import { CurrencyIcon } from '../CurrencyIcon'
import { CoreSelectWrapper } from './coreSelect.style'

export const CoreSelect = ({
  LeftIcon,
  label,
  name,
  type = 'default',
  options,
  onChange,
  required,
  error,
  hint,
  className,
  hideCaption,
  value,
  disabled,
  loading,
  allAccounts = false,
  isAccountsLoading = false
}: SelectionProps) => {
  const [inputFocused, setInputFocused] = useState<boolean>(false)
  const [filterString, setFilterString] = useState<string>('')
  const [filteredOptions, setFilteredOptions] = useState<any[]>([])
  const [selectedOption, setSelectedOption] = useState<any>(null)
  const [offsetErrorHeight, setOffsetErrorHeight] = useState<number>(0)
  const [offsetHintHeight, setOffsetHintHeight] = useState<number>(0)
  const [blockBlur, setBlockBlur] = useState<boolean>(false)

  const inputRef = useRef(null)

  useEffect(() => {
    setFilteredOptions(filterOptions(type, options, filterString))
  }, [filterString])

  useEffect(() => {
    setFilterString('')

    if (inputRef && inputRef.current) {
      const error = inputRef.current.querySelector('.core-input-error')
      const hint = inputRef.current.querySelector('.core-input-hint')
      setOffsetErrorHeight(error ? error.offsetHeight : 0)
      setOffsetHintHeight(hint ? hint.offsetHeight : 0)
    }
  }, [inputFocused])

  useEffect(() => {
    if (isValidArray(options)) {
      setFilteredOptions(options)
    }
  }, [options])

  useMemo(() => {
    if (!value || value?.length === 0) {
      setSelectedOption(null)
      return
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const foundOption = options.find(option => option.value == value)
      if (foundOption) {
        setSelectedOption(foundOption)
        return
      }
    }
    setSelectedOption(value)
  }, [value, options])

  return (
    <CoreSelectWrapper ref={inputRef} className={cn(className, { disabled })}>
      {!inputFocused && selectedOption ? (
        <div
          className='selected-content'
          onClick={ev => {
            ev.preventDefault()
            ev.stopPropagation()
            setInputFocused(true)
          }}>
          <SelectionElement
            data-test='selected-content'
            data={selectedOption}
            type={type}
            isCountryType={type === 'country'}
            setInputFocused={setInputFocused}
            isAccountsLoading={isAccountsLoading}
            isSelected
            hideCaption={hideCaption}
          />
        </div>
      ) : (
        <CoreInput
          LeftIcon={
            (selectedOption && type !== 'default') || (type === 'account' && isAccountsLoading) ? (
              selectedOption && type === 'currency' ? (
                <CurrencyIcon currency={selectedOption?.value} size='32px' circle />
              ) : (
                <SelectedIcon isLoading={isAccountsLoading} type={type} selectedOption={selectedOption} />
              )
            ) : (
              LeftIcon
            )
          }
          label={selectedOption ? selectedLabel(type, selectedOption) : label}
          RightIcon={
            loading ? <></> : inputFocused ? <img src={arrowUpIcon} alt='' /> : <img src={arrowDownIcon} alt='' />
          }
          setFocus={setInputFocused}
          name={name}
          onChange={(_, value: string) => setFilterString(value)}
          focused={inputFocused}
          required={required}
          blockBlur={blockBlur}
          error={error}
          disabled={disabled}
          hint={hint}
          onKeyDown={e => {
            if (e.key === 'Enter' && filteredOptions.length === 1) {
              if ((type === 'country' || type === 'default') && 'value' in filteredOptions[0]) {
                onChange && onChange(name, filteredOptions[0]?.value)
              } else {
                onChange && onChange(name, filteredOptions[0])
              }
              setSelectedOption(filteredOptions[0])
              setInputFocused(false)
            }
          }}
        />
      )}
      {inputFocused && (
        <div
          onMouseDown={() => {
            setBlockBlur(true)
          }}
          onMouseOut={() => setBlockBlur(false)}
          style={{ marginTop: -offsetErrorHeight + -offsetHintHeight }}
          className='core-selection-data-container'>
          {type === 'account' && allAccounts && (
            <SelectionElement
              data-test='selected-content'
              key='all-accounts'
              data='all-accounts'
              type='account'
              isAccountsLoading={isAccountsLoading}
              hideCaption={false}
              onSelect={() => {
                onChange && onChange(name, null)
                setSelectedOption(null)
                setInputFocused(false)
              }}
            />
          )}
          <>
            {isValidArray(options)
              ? filteredOptions.map((option, key) => {
                  return (
                    <SelectionElement
                      data-test='selected-content'
                      key={key}
                      data={option}
                      type={type}
                      hideCaption={hideCaption}
                      onSelect={selectedData => {
                        if ((type === 'country' || type === 'default') && 'value' in selectedData) {
                          onChange && onChange(name, selectedData?.value)
                        } else {
                          onChange && onChange(name, selectedData)
                        }
                        setSelectedOption(selectedData)
                        setInputFocused(false)
                      }}
                    />
                  )
                })
              : !isAccountsLoading && (
                  <div className='core-selections-no-options'>
                    {tr('CHECKOUT.SELECT.NO_VALID_OPTIONS', 'No valid options')}
                  </div>
                )}
          </>
        </div>
      )}
    </CoreSelectWrapper>
  )
}
